/**
 * Brand configuration — single source of truth for customer-facing branding.
 *
 * The MSD business is preparing for a customer-facing rebrand. The legal
 * entity remains "Merchant Service Depot, LLC"; only the public-facing brand
 * name and short handle change.
 *
 * # Three-variable model
 *
 *   - `legalName`   — the LLC entity name, used in legal contexts (contracts,
 *                     copyright footers). `null` today because the brand name
 *                     IS the legal name (no DBA distinction needed yet).
 *                     Post-rebrand, set to `"Merchant Service Depot, LLC"`.
 *   - `dbaName`     — the public-facing brand name. Today: "Merchant Service
 *                     Depot". Post-rebrand: the new brand.
 *   - `handle`      — short / casual form (used in product names like
 *                     "MSD Edge", "MSD Products"). Today: "MSD".
 *
 * # Cutover process (when the new brand is ready)
 *
 *   1. Update `legalName`, `dbaName`, `handle` here.
 *   2. Drop the new logo / favicon / OG image / playbook PDFs into `public/`
 *      and update the paths in `BRAND.assets` here.
 *   3. Set `showFormerlyTransitionLabel = true` so email "From" senders read
 *      "<NewBrand> Edge (formerly MSD Edge)" during the transition window.
 *   4. `npm run build` → deploy.
 *   5. After deploy, hit `/admin/testing` → "Refresh App for All Sessions"
 *      to flush stale caches in any active browser sessions.
 *   6. After several months, set `showFormerlyTransitionLabel = false` to
 *      drop the "(formerly MSD Edge)" suffix.
 *
 * # External system identifiers — DO NOT confuse with brand strings
 *
 *   The strings `"MSD_PORTAL"` (application source code) and `"MSD-"` (app
 *   number prefix) appear in calls to Pulsepoint and EMS integrations. Those
 *   are *external system identifiers* expected by the third-party APIs — they
 *   stay as-is regardless of the rebrand. Do not refactor them through this
 *   config.
 */

interface BrandColors {
  /** Primary brand color (formerly `MSD_NAVY` = #062665). RGB tuple for jsPDF. */
  readonly primary: readonly [number, number, number];
  /** Accent brand color (formerly `MSD_ORANGE` = #ee7232). RGB tuple for jsPDF. */
  readonly accent: readonly [number, number, number];
  /** CSS hex form of `primary`. */
  readonly primaryHex: string;
  /** CSS hex form of `accent`. */
  readonly accentHex: string;
}

interface BrandAssets {
  readonly logo: string;
  /** Alt text for screen readers and broken-image fallbacks. */
  readonly logoAlt: string;
  /** Favicon (browser tab icon). */
  readonly favicon: string;
  /** OpenGraph share image. */
  readonly ogImage: string;
  /** Marketing playbook PDF linked from the dashboard welcome banner. */
  readonly playbookPdf: string;
}

interface BrandUrls {
  /** Public-facing portal (the URL agents and merchants visit). */
  readonly portal: string;
  /** Marketing / public marketing site. */
  readonly marketing: string;
  /** Gateway wiki subdomain. */
  readonly wiki: string;
}

interface BrandEmails {
  /** General support inbox. */
  readonly support: string;
  /** Onboarding / paper-app inbox. */
  readonly onboarding: string;
  /** Sales inbox. */
  readonly sales: string;
  /** Default signer / agreement-author identity. */
  readonly signer: string;
}

interface BrandSigner {
  /** Name on the legal-entity side of agreements (DocuSeal etc.). */
  readonly name: string;
  /** Email used for signer routing. Mirrors `BRAND.emails.signer`. */
  readonly email: string;
}

export interface BrandConfig {
  /**
   * Legal entity name. `null` when the brand IS the legal entity (no DBA);
   * set to a string like "Merchant Service Depot, LLC" once the brand is
   * legally distinct from the operating company.
   */
  readonly legalName: string | null;
  /** Public-facing brand name. */
  readonly dbaName: string;
  /** Short handle / abbreviated form. */
  readonly handle: string;
  /** Tagline appended to brand+handle pairings ("MSD Edge"). */
  readonly tagline: string;
  /** Apex domain. */
  readonly domain: string;
  /**
   * When `true`, email "From" sender names render as
   * `"<dbaName> <tagline> (formerly MSD Edge)"` to ease the transition for
   * recipients. Toggle off after a few months once recipients have adjusted.
   */
  readonly showFormerlyTransitionLabel: boolean;
  readonly colors: BrandColors;
  readonly assets: BrandAssets;
  readonly urls: BrandUrls;
  readonly emails: BrandEmails;
  readonly signer: BrandSigner;
}

export const BRAND: BrandConfig = {
  legalName: null,
  dbaName: "Merchant Service Depot",
  handle: "MSD",
  tagline: "Edge",
  domain: "merchantservicedepot.com",
  showFormerlyTransitionLabel: false,

  colors: {
    primary: [26, 23, 20],
    accent: [217, 142, 61],
    primaryHex: "#1A1714",
    accentHex: "#D98E3D",
  },

  assets: {
    logo: "/brand/myela-mark-capsules-signal.svg",
    logoAlt: "Myela",
    favicon: "/favicon.svg",
    ogImage: "/brand/myela-lockup-horizontal-ink.svg",
    playbookPdf: "/MSD-Edge-Agent-Portal-Playbook_v1.2.pdf",
  },

  urls: {
    portal: "https://agent.merchantservicedepot.com",
    marketing: "https://merchantservicedepot.com",
    wiki: "https://wiki.merchantservicedepot.com",
  },

  emails: {
    support: "support@merchantservicedepot.com",
    onboarding: "onboarding@merchantservicedepot.com",
    sales: "sales@merchantservicedepot.com",
    signer: "support@merchantservicedepot.com",
  },

  signer: {
    name: "Merchant Service Depot, LLC",
    email: "support@merchantservicedepot.com",
  },
} as const;

// ---------------------------------------------------------------------------
// Computed helpers
// ---------------------------------------------------------------------------

/**
 * The full product label — e.g. "MSD Edge" today, "<NewBrand> Edge"
 * post-rebrand. Used in headers, login screens, page titles.
 */
export function productName(brand: BrandConfig = BRAND): string {
  return `${brand.handle} ${brand.tagline}`;
}

/**
 * Legal entity attribution. When `legalName` is set, renders the DBA form;
 * otherwise treats `dbaName` as the legal entity (status quo today).
 *
 * Today  → "Merchant Service Depot, LLC"
 * Future → "Merchant Service Depot, LLC dba <NewBrand>"
 */
export function legalEntityName(brand: BrandConfig = BRAND): string {
  if (brand.legalName) {
    return `${brand.legalName} dba ${brand.dbaName}`;
  }
  return `${brand.dbaName}, LLC`;
}

/**
 * Email "From" sender-name component. Pair with an email address as
 * `${fromName()} <${BRAND.emails.support}>`.
 *
 * Today  → "Merchant Service Depot Edge"
 * Transition → "<NewBrand> Edge (formerly MSD Edge)"
 * Settled → "<NewBrand> Edge"
 */
export function fromName(brand: BrandConfig = BRAND): string {
  const base = `${brand.dbaName} ${brand.tagline}`;
  return brand.showFormerlyTransitionLabel
    ? `${base} (formerly MSD Edge)`
    : base;
}

/**
 * Full RFC-style "From" header — name + email. Pass a specific inbox key to
 * override the default support address.
 */
export function fromHeader(
  inbox: keyof BrandEmails = "support",
  brand: BrandConfig = BRAND,
): string {
  return `${fromName(brand)} <${brand.emails[inbox]}>`;
}

/**
 * "<Handle> <suffix>" product label — e.g. "MSD Products", "MSD Acquiring",
 * "MSD Payment Solutions".
 */
export function productLabel(
  suffix: string,
  brand: BrandConfig = BRAND,
): string {
  return `${brand.handle} ${suffix}`;
}

/**
 * Copyright footer line (e.g. for marketing emails / static pages).
 *
 * Today  → "© <year> Merchant Service Depot"
 * Future → "© <year> Merchant Service Depot, LLC dba <NewBrand>"
 */
export function copyrightLine(
  year: number = new Date().getFullYear(),
  brand: BrandConfig = BRAND,
): string {
  const entity = brand.legalName ? legalEntityName(brand) : brand.dbaName;
  return `© ${year} ${entity}`;
}
